/**
 * Cordn crypto layer.
 * ─────────────────────────────────────────────────────────────────────────────
 * Seals / opens a crew group message. This is the ONLY place that touches
 * ciphertext, so upgrading to a full MLS ratchet (`ts-mls`: encrypt-to-epoch /
 * decrypt-from-epoch) is a drop-in replacement that leaves transport + UI intact.
 *
 * CURRENT scheme (pre-MLS, honest): NIP-44 group delivery. The sender encrypts
 * the plaintext once per roster recipient (and once to self) using their signer's
 * real NIP-44, producing a `recipients` map on the envelope. Any roster member
 * opens the message by decrypting the entry addressed to them. This gives real
 * end-to-end confidentiality against the coordinator and relays; it does NOT yet
 * provide MLS forward secrecy / post-compromise security — that arrives with the
 * ts-mls upgrade. The seam (this file's two functions) does not change.
 */

import type { NostrEvent } from '@nostrify/nostrify';
import { verifyEvent } from 'nostr-tools';

/**
 * The signer subset we need: NIP-44 encrypt/decrypt for confidentiality, plus
 * `signEvent` so the sender can produce a signed **author proof** that survives
 * an ephemeral (throwaway) outer envelope key. Matches window.nostr / NUser.
 */
export interface Nip44Signer {
  nip44: {
    encrypt: (pubkey: string, plaintext: string) => Promise<string>;
    decrypt: (pubkey: string, ciphertext: string) => Promise<string>;
  };
  signEvent: (event: {
    kind: number;
    content: string;
    created_at: number;
    tags: string[][];
  }) => Promise<NostrEvent>;
}

/** Kind of the inner "author proof" rumor the sender signs (never published raw). */
const CORDN_INNER_KIND = 4498;

/** Inner plaintext payload before encryption. */
interface CordnPlaintext {
  /** Message body. */
  t: string;
  /** Sender pubkey (hex) — bound inside the ciphertext so it can't be spoofed. */
  s: string;
  /** Client timestamp (unix seconds). */
  ts: number;
  /**
   * Author proof (v2 only): a fully-signed Nostr event by the REAL sender whose
   * `content` is the message body. Because it carries the sender's own schnorr
   * signature, any roster member can verify authorship after decrypting — even
   * though the outer Cordn envelope is signed by a throwaway ephemeral key the
   * coordinator can't link to anyone. Absent in v1 (which trusted the outer
   * envelope signature and therefore leaked the sender to the coordinator).
   */
  proof?: NostrEvent;
}

/** The sealed content shape stored in the envelope event's `content`. */
export interface CordnSealed {
  v: 1 | 2;
  /**
   * Encryption scheme marker.
   *  - `nip44-group`    (v1): sender-signed outer envelope; coordinator sees the
   *                     sender pubkey. Kept readable for backward compatibility.
   *  - `nip44-group-v2` (v2): ephemeral-signed outer envelope (coordinator never
   *                     learns who is talking) + a signed in-ciphertext author
   *                     proof so authorship is still unforgeable.
   *  - `mls` (future): full RFC 9420 ratchet.
   */
  alg: 'nip44-group' | 'nip44-group-v2';
  /** recipientPubkey → NIP-44 ciphertext of the same CordnPlaintext. */
  recipients: Record<string, string>;
}

/**
 * Seal a plaintext message for the whole roster.
 *
 * @param signer     the sender's NIP-44 signer
 * @param senderPk   sender pubkey (hex)
 * @param roster     all recipient pubkeys (hex); the sender is added automatically
 * @param text       message body
 */
export async function sealGroupMessage(
  signer: Nip44Signer,
  senderPk: string,
  roster: string[],
  text: string,
  groupId: string,
): Promise<CordnSealed> {
  const ts = Math.floor(Date.now() / 1000);

  // Author proof: the REAL sender signs an inner rumor carrying the message.
  // This is what makes authorship unforgeable once the outer envelope is signed
  // by a throwaway ephemeral key (so the coordinator can't see the sender). The
  // `a` tag binds the proof to THIS crew, so it can't be replayed into another.
  const proof = await signer.signEvent({
    kind: CORDN_INNER_KIND,
    content: text,
    created_at: ts,
    tags: [
      ['a', groupId],
      ['alt', 'Cordn crew chat author proof'],
    ],
  });

  const plaintext: CordnPlaintext = { t: text, s: senderPk.toLowerCase(), ts, proof };
  const json = JSON.stringify(plaintext);

  // Encrypt once per unique recipient (roster ∪ {sender}).
  const targets = Array.from(new Set([senderPk.toLowerCase(), ...roster.map((r) => r.toLowerCase())]));
  const recipients: Record<string, string> = {};
  await Promise.all(
    targets.map(async (pk) => {
      recipients[pk] = await signer.nip44.encrypt(pk, json);
    }),
  );

  return { v: 2, alg: 'nip44-group-v2', recipients };
}

/**
 * Open a sealed group message as `viewerPk`. Returns the plaintext body + the
 * (verified) sender, or null if this viewer isn't a recipient / decryption fails
 * / the author proof doesn't check out.
 *
 * Handles both envelope versions:
 *  - v1 (`nip44-group`): trusts the caller to bind the sender to the outer
 *    envelope pubkey (the transport layer does this).
 *  - v2 (`nip44-group-v2`): verifies the embedded signed author proof, so the
 *    real sender is established from the ciphertext alone — the outer envelope
 *    can safely be signed by a throwaway ephemeral key.
 */
export async function openGroupMessage(
  signer: Nip44Signer,
  viewerPk: string,
  sealed: CordnSealed,
  groupId: string,
): Promise<{ content: string; sender: string; ts: number; verified: boolean } | null> {
  const alg = sealed.alg;
  if (alg !== 'nip44-group' && alg !== 'nip44-group-v2') return null;
  const ct = sealed.recipients[viewerPk.toLowerCase()];
  if (!ct) return null;
  try {
    const json = await signer.nip44.decrypt(viewerPk, ct);
    const parsed = JSON.parse(json) as Partial<CordnPlaintext>;
    if (typeof parsed.t !== 'string' || typeof parsed.s !== 'string') return null;
    const sender = parsed.s.toLowerCase();
    const ts = parsed.ts ?? Math.floor(Date.now() / 1000);

    if (alg === 'nip44-group-v2') {
      // Verify the signed author proof: it must be a valid event, authored by
      // the claimed sender, whose content matches the message body, and bound to
      // THIS crew (so a proof can't be lifted from another group).
      const proof = parsed.proof;
      const proofGroup = proof?.tags.find(([t]) => t === 'a')?.[1];
      if (
        !proof ||
        proof.kind !== CORDN_INNER_KIND ||
        proof.pubkey?.toLowerCase() !== sender ||
        proof.content !== parsed.t ||
        proofGroup !== groupId ||
        !verifyEvent(proof)
      ) {
        return null;
      }
      return { content: parsed.t, sender, ts, verified: true };
    }

    // v1: authorship is verified by the transport layer against the outer key.
    return { content: parsed.t, sender, ts, verified: false };
  } catch {
    return null;
  }
}

/** Safely parse an envelope's content into a CordnSealed, or null. */
export function parseSealed(content: string): CordnSealed | null {
  try {
    const obj = JSON.parse(content) as Partial<CordnSealed>;
    const algOk = obj.alg === 'nip44-group' || obj.alg === 'nip44-group-v2';
    const vOk = obj.v === 1 || obj.v === 2;
    if (!algOk || !vOk || typeof obj.recipients !== 'object' || !obj.recipients) {
      return null;
    }
    return obj as CordnSealed;
  } catch {
    return null;
  }
}
